/**
 * @module i18n
 */
let translations = {};

/**
 * Loads the base language file for the application.
 * @param {string} language - The two-letter language code.
 */
export async function initI18n(language = 'en') {
  try {
    const response = await fetch(`/assets/languages/maps-${language}.json`);
    translations = await response.json();
  } catch (error) {
    console.warn(`Base language file for '${language}' not found.`, error);
    // Attempt to load English as a fallback
    if (language !== 'en') {
      await initI18n('en');
    }
    translations = {};
  }
}

/**
 * Loads and merges language files from a list of product configurations.
 * @param {Array<object>} productConfigs - An array of loaded product configs.
 * @param {string} language - The current language.
 */
export async function mergeProductLanguages(productConfigs, language = 'en') {
  const fetchPromises = [];
  for (const config of productConfigs) {
    const languagePath = `/maps/${config.product}/languages`;
    const languageFileUrl = `${languagePath}/${language}.json`;
    // Fetch each language file, but don't fail the whole process if one 
    // is missing
    fetchPromises.push(
      fetch(languageFileUrl)
        .then(res => res.ok ? res.json() : {}) // Return empty object on failure
        .catch(() => ({})) // Return empty object on network error
    );
  }
  // Fetch them all asynchronously for speed
  const productTranslations = await Promise.all(fetchPromises);
  // Merge all fetched translations into the main dictionary
  // This allows product-specific keys to override base keys if needed.
  for (const prodTrans of productTranslations) {
    Object.assign(translations, prodTrans);
  }
}

/**
 * Translates a key using the fully merged dictionary.
 * @param {string} key - The key to translate (e.g., "zoom_in_tip").
 * @returns {string} The translated string, or the key itself if not found.
 */
export function t(key) {
  return translations[key] || `[${key}]`; // Return key in brackets if not found for easy debugging
}